import { HTMLAttributes, forwardRef, ReactNode } from 'react'
import {
  Notification,
  NotificationType,
  SvgIcon,
  SvgIconType,
  Portal,
} from '@integration-app/react'
import clsx from 'clsx'

import { PropertiesPortal } from './Properties'
import {
  SchemaBuilderRowContextProvider,
  useSchemaBuilderRowContext,
} from './row-context'
import classes from './Row.module.css'

interface IRowRoot extends HTMLAttributes<HTMLDivElement> {
  embedded?: boolean
}

function RowRoot({ children, className, embedded }: IRowRoot) {
  return (
    <SchemaBuilderRowContextProvider>
      <div
        className={clsx(
          classes.rowWrapper,
          embedded && classes.rowWrapperEmbedded,
        )}
      >
        <div
          className={clsx(
            classes.row,
            embedded && classes.rowEmbedded,
            className,
          )}
        >
          {children}
        </div>
        <PropertiesPortal />
      </div>
    </SchemaBuilderRowContextProvider>
  )
}

interface IRowProperties extends HTMLAttributes<HTMLDivElement> {
  expanded?: boolean
}

const RowProperties = forwardRef<HTMLDivElement, IRowProperties>(
  ({ children, className, expanded, ...props }, ref) => {
    const { propertiesRef } = useSchemaBuilderRowContext()

    return (
      <Portal node={propertiesRef}>
        <div
          className={clsx(
            classes.rowProperties,
            expanded && classes.rowPropertiesExpanded,
            className,
          )}
          {...props}
          ref={ref}
        >
          {children}
        </div>
      </Portal>
    )
  },
)
RowProperties.displayName = 'RowProperties'

export const Row = Object.assign(RowRoot, {
  Properties: RowProperties,
})

interface ICell extends HTMLAttributes<HTMLDivElement> {
  width?: string
  grow?: boolean
  shrink?: boolean
  disabled?: boolean
}

interface ICellIcon extends Omit<ICell, 'children'> {
  type: SvgIconType
}

const CellRoot = forwardRef<HTMLDivElement, ICell>(
  ({ children, disabled, grow, shrink, width, className, ...props }, ref) => {
    const style = width ? { flexBasis: width } : undefined

    return (
      <div
        className={clsx(
          classes.cell,
          grow && classes.cellGrow,
          shrink && classes.cellShrink,
          disabled && classes.cellDisabled,
          className,
        )}
        aria-disabled={disabled}
        aria-readonly={disabled}
        style={style}
        {...props}
        ref={ref}
      >
        {children}
      </div>
    )
  },
)
CellRoot.displayName = 'CellRoot'

const CellName = forwardRef<HTMLDivElement, ICell>(
  ({ children, className, ...props }, ref) => {
    return (
      <Cell className={clsx(classes.cellName, className)} {...props} ref={ref}>
        {children}
      </Cell>
    )
  },
)
CellName.displayName = 'CellName'

const CellCombobox = forwardRef<HTMLDivElement, ICell>(
  ({ children, className, ...props }, ref) => {
    return (
      <Cell
        className={clsx(classes.cellCombobox, className)}
        {...props}
        ref={ref}
      >
        {children}
      </Cell>
    )
  },
)
CellCombobox.displayName = 'CellCombobox'

const CellInput = forwardRef<HTMLDivElement, ICell>(
  ({ children, className, ...props }, ref) => {
    return (
      <Cell className={clsx(classes.cellInput, className)} {...props} ref={ref}>
        {children}
      </Cell>
    )
  },
)
CellInput.displayName = 'CellInput'

const CellIcon = forwardRef<HTMLDivElement, ICellIcon>(
  ({ type, className, ...props }, ref) => {
    return (
      <Cell className={clsx(classes.cellIcon, className)} {...props} ref={ref}>
        <SvgIcon type={type} />
      </Cell>
    )
  },
)
CellIcon.displayName = 'CellIcon'

export const Cell = Object.assign(CellRoot, {
  Name: CellName,
  Combobox: CellCombobox,
  Input: CellInput,
  Tag: CellCombobox,
  Icon: CellIcon,
  Value: CellRoot,
})

interface ITitle extends HTMLAttributes<HTMLDivElement> {
  truncate?: boolean
  required?: boolean
}

const TitleRoot = forwardRef<HTMLDivElement, ITitle>(
  ({ children, truncate, ...props }, ref) => {
    return (
      <div className={classes.title} {...props} ref={ref}>
        {truncate ? (
          <span className={classes.titleTruncate}>{children}</span>
        ) : (
          <>{children}</>
        )}
      </div>
    )
  },
)
TitleRoot.displayName = 'Title'

const TitleRequired = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ ...props }, ref) => {
  return (
    <span className={classes.titleRequiredSign} {...props} ref={ref}>
      *
    </span>
  )
})
TitleRequired.displayName = 'TitleRequired'

const TitleErrorDot = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ ...props }, ref) => {
  return <span className={classes.titleErrorDot} {...props} ref={ref} />
})
TitleErrorDot.displayName = 'TitleErrorDot'

interface ITitleBadge extends HTMLAttributes<HTMLSpanElement> {
  align?: 'left' | 'right'
  tooltip: ReactNode
  type?: NotificationType
}

const TitleBadge = ({
  tooltip,
  align = 'left',
  type = 'info',
  ...props
}: ITitleBadge) => {
  return (
    <Notification
      mode={'icon'}
      className={clsx(
        classes.titleBadge,
        align === 'right' && classes.titleBadgeRightSide,
      )}
      type={type}
      {...props}
    >
      {tooltip}
    </Notification>
  )
}

export const Title = Object.assign(TitleRoot, {
  Required: TitleRequired,
  ErrorDot: TitleErrorDot,
  Badge: TitleBadge,
})
